import type { JSX } from 'react';

/**
 * A soft, localized glow parked inside a section (which is `position: relative`). Unlike the fixed global
 * {@link LandingAtmosphere} aurora — a constant wash — these scroll WITH their section, and we alternate
 * their warm/cool tone and side down the page so scrolling reveals a changing light. THAT is what turns the
 * old "one dead navy tunnel" into distinct chapters: a warm room, then a cool one, then warm again.
 *
 * Drop it as the FIRST child of a section so it paints behind the content (DOM order = paint order within
 * the same stacking context). Purely decorative → `aria-hidden`, `pointer-events-none`. No animation, so it
 * costs nothing and needs no reduced-motion gate.
 */
export function SectionGlow({
  tone = 'warm',
  side = 'left',
  className = '',
}: {
  tone?: 'warm' | 'cool';
  side?: 'left' | 'right' | 'center';
  className?: string;
}): JSX.Element {
  const color = tone === 'cool' ? 'rgba(120,128,255,0.28)' : 'rgba(255,193,58,0.28)';
  // Pool the light near the TOP of the section, where the eyebrow + heading sit — so each chapter's title
  // rises out of a soft coloured glow (reliably visible over the near-black base, unlike a faint centred blob).
  const pos =
    side === 'left'
      ? 'left-[6%] -translate-x-1/3'
      : side === 'right'
        ? 'right-[6%] translate-x-1/3'
        : 'left-1/2 -translate-x-1/2';
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute -top-[6%] h-[46vh] max-h-[520px] w-[46vh] max-w-[520px] rounded-full blur-[90px] ${pos} ${className}`}
      style={{ background: `radial-gradient(circle, ${color}, transparent 68%)` }}
    />
  );
}
